import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, FileText } from 'lucide-react';

const NoteAuteurEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [ribs, setRibs] = useState([]);
  
  const [formData, setFormData] = useState({
    client_id: '',
    date: new Date().toISOString().split('T')[0],
    objet: '',
    echeance: 'À la réception',
    emetteur: {
      use_kolnoa: true,
      nom: 'FERRARA Jeremy',
      adresse: '6, Rue du Pic de Barrette',
      ville: '75015 Paris',
      telephone: '06.68.11.93.43'
    },
    lines: [{ description: '', montant_ttc: 0 }],
    selected_rib_id: ''
  });

  useEffect(() => {
    loadClients();
    loadRibs();
    if (id && id !== 'new') {
      loadNote();
    }
  }, [id]);

  const loadClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(res.data);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Erreur lors du chargement des clients');
    }
  };

  const loadRibs = async () => {
    try {
      const res = await axios.get(`${API}/ribs`, { withCredentials: true });
      setRibs(res.data);
      // Set default RIB if exists and no RIB is selected
      const defaultRib = res.data.find(r => r.is_default);
      if (defaultRib && !formData.selected_rib_id) {
        setFormData(prev => ({ ...prev, selected_rib_id: defaultRib.id }));
      }
    } catch (error) {
      console.error('Error loading RIBs:', error);
    }
  };

  const loadNote = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notes-auteur/${id}`, { withCredentials: true });
      setFormData({
        client_id: res.data.client_id,
        date: res.data.date,
        objet: res.data.objet,
        echeance: res.data.echeance,
        emetteur: res.data.emetteur,
        lines: res.data.lines
      });
    } catch (error) {
      console.error('Error loading note:', error);
      toast.error('Erreur lors du chargement de la note');
      navigate('/backoffice/notes-auteur');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmetteurChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      emetteur: { ...prev.emetteur, [field]: value }
    }));
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = field === 'montant_ttc' ? parseFloat(value) || 0 : value;
    setFormData(prev => ({ ...prev, lines: newLines }));
  };

  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { description: '', montant_ttc: 0 }]
    }));
  };

  const removeLine = (index) => {
    if (formData.lines.length === 1) {
      toast.error('Il faut au moins une ligne');
      return;
    }
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, lines: newLines }));
  };

  const calculateTotals = () => {
    const total_ttc = formData.lines.reduce((sum, line) => sum + (line.montant_ttc || 0), 0);
    const csg_crds = total_ttc * 0.0975;
    const part_a_regler = total_ttc - csg_crds;
    const agessa = (total_ttc * 0.0035) + 5.25;
    const net_a_payer = total_ttc - csg_crds - agessa;

    return {
      total_ttc: total_ttc.toFixed(2),
      csg_crds: csg_crds.toFixed(2),
      part_a_regler: part_a_regler.toFixed(2),
      agessa: agessa.toFixed(2),
      net_a_payer: net_a_payer.toFixed(2)
    };
  };

  const handleSave = async () => {
    // Validation
    if (!formData.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    if (!formData.objet) {
      toast.error('Veuillez saisir un objet');
      return;
    }
    if (formData.lines.length === 0 || formData.lines.every(l => !l.description)) {
      toast.error('Veuillez ajouter au moins une prestation');
      return;
    }

    setSaving(true);
    try {
      if (id && id !== 'new') {
        await axios.put(`${API}/notes-auteur/${id}`, formData, { withCredentials: true });
        toast.success('Note d\'auteur mise à jour');
      } else {
        await axios.post(`${API}/notes-auteur`, formData, { withCredentials: true });
        toast.success('Note d\'auteur créée');
      }
      navigate('/backoffice/notes-auteur');
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const selectedClient = clients.find(c => c.id === formData.client_id);
  const totals = calculateTotals();

  if (loading) {
    return <div className="p-6">Chargement...</div>;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/backoffice/notes-auteur')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux notes
        </Button>
        <h1 className="text-2xl md:text-3xl font-bold text-stone-800 flex items-center gap-2">
          <FileText className="h-7 w-7 text-amber-600" />
          {id === 'new' ? 'Nouvelle Note d\'Auteur' : 'Modifier Note d\'Auteur'}
        </h1>
      </div>

      <div className="space-y-6">
        {/* Client & Date */}
        <Card>
          <CardHeader>
            <CardTitle>Informations Générales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="client">Client *</Label>
                <Select value={formData.client_id} onValueChange={(val) => handleInputChange('client_id', val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.prenom} {client.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedClient && (
                  <p className="text-xs text-stone-500 mt-1">
                    {selectedClient.email}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="objet">Objet *</Label>
              <Input
                id="objet"
                value={formData.objet}
                onChange={(e) => handleInputChange('objet', e.target.value)}
                placeholder="Ex: Réalisation de Masters DVD"
              />
            </div>

            <div>
              <Label htmlFor="echeance">Échéance</Label>
              <Select value={formData.echeance} onValueChange={(val) => handleInputChange('echeance', val)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="À la réception">À la réception</SelectItem>
                  <SelectItem value="30 jours">30 jours</SelectItem>
                  <SelectItem value="60 jours">60 jours</SelectItem>
                  <SelectItem value="custom">Personnalisé</SelectItem>
                </SelectContent>
              </Select>
              {formData.echeance === 'custom' && (
                <Input
                  className="mt-2"
                  value={formData.echeance}
                  onChange={(e) => handleInputChange('echeance', e.target.value)}
                  placeholder="Saisissez l'échéance"
                />
              )}
            </div>

            <div>
              <Label htmlFor="selected_rib">RIB à inclure dans le PDF</Label>
              <Select value={formData.selected_rib_id} onValueChange={(val) => handleInputChange('selected_rib_id', val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un RIB" />
                </SelectTrigger>
                <SelectContent>
                  {ribs && ribs.map((rib) => (
                    <SelectItem key={rib.id} value={rib.id}>
                      {rib.label} {rib.is_default && '(Par défaut)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500 mt-1">
                Le RIB sélectionné apparaîtra en pied de page du PDF
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Émetteur */}
        <Card>
          <CardHeader>
            <CardTitle>Émetteur</CardTitle>
            <CardDescription>Choisissez d'émettre via Kolnoa ou avec vos coordonnées personnelles</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium">Émettre via Kolnoa</div>
                <p className="text-sm text-stone-600">Utiliser les coordonnées de l'entreprise</p>
              </div>
              <Switch
                checked={formData.emetteur.use_kolnoa}
                onCheckedChange={(checked) => handleEmetteurChange('use_kolnoa', checked)}
              />
            </div>

            {!formData.emetteur.use_kolnoa && (
              <div className="space-y-4 p-4 bg-stone-50 rounded-lg">
                <div>
                  <Label htmlFor="emetteur_nom">Nom complet</Label>
                  <Input
                    id="emetteur_nom"
                    value={formData.emetteur.nom}
                    onChange={(e) => handleEmetteurChange('nom', e.target.value)}
                    placeholder="FERRARA Jeremy"
                  />
                </div>
                <div>
                  <Label htmlFor="emetteur_adresse">Adresse</Label>
                  <Input
                    id="emetteur_adresse"
                    value={formData.emetteur.adresse}
                    onChange={(e) => handleEmetteurChange('adresse', e.target.value)}
                    placeholder="6, Rue du Pic de Barrette"
                  />
                </div>
                <div>
                  <Label htmlFor="emetteur_ville">Ville</Label>
                  <Input
                    id="emetteur_ville"
                    value={formData.emetteur.ville}
                    onChange={(e) => handleEmetteurChange('ville', e.target.value)}
                    placeholder="75015 Paris"
                  />
                </div>
                <div>
                  <Label htmlFor="emetteur_tel">Téléphone</Label>
                  <Input
                    id="emetteur_tel"
                    value={formData.emetteur.telephone}
                    onChange={(e) => handleEmetteurChange('telephone', e.target.value)}
                    placeholder="06.68.11.93.43"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prestations */}
        <Card>
          <CardHeader>
            <CardTitle>Prestations</CardTitle>
            <CardDescription>Ajoutez les articles/prestations</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.lines.map((line, index) => (
              <div key={index} className="flex gap-2 items-start">
                <div className="flex-1">
                  <Label className="text-xs">Description</Label>
                  <Textarea
                    value={line.description}
                    onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                    placeholder="Master Vidéo et Synchro VF Sur 3 Films..."
                    rows={2}
                  />
                </div>
                <div className="w-32">
                  <Label className="text-xs">Montant TTC</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={line.montant_ttc}
                    onChange={(e) => handleLineChange(index, 'montant_ttc', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeLine(index)}
                  className="mt-6 text-rose-600 hover:text-rose-700"
                  disabled={formData.lines.length === 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={addLine} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter une ligne
            </Button>
          </CardContent>
        </Card>

        {/* Calculs */}
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader>
            <CardTitle className="text-amber-900">Calculs Automatiques</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 font-mono text-sm">
              <div className="flex justify-between py-2 border-b border-amber-200">
                <span className="font-semibold">TOTAL TTC</span>
                <span className="font-bold">{totals.total_ttc}€</span>
              </div>
              <div className="flex justify-between py-1 text-stone-600">
                <span>CSG/CRDS (9.75%)</span>
                <span>-{totals.csg_crds}€</span>
              </div>
              <div className="flex justify-between py-2 border-b border-amber-200">
                <span className="font-semibold">Part à régler</span>
                <span className="font-semibold">{totals.part_a_regler}€</span>
              </div>
              <div className="flex justify-between py-1 text-stone-600">
                <span>AGESSA (0.35% + 5.25€)</span>
                <span>-{totals.agessa}€</span>
              </div>
              <div className="flex justify-between py-3 border-t-2 border-amber-300 text-lg">
                <span className="font-bold text-amber-900">NET À PAYER</span>
                <span className="font-bold text-green-700">{totals.net_a_payer}€</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/backoffice/notes-auteur')}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NoteAuteurEditor;
